import { API_BASE } from '@/lib/config'

export interface GoogleSignInResult {
  success: boolean
  error?: string
}

function getAuthUrl(): string {
  if (API_BASE.startsWith('http')) {
    return `${API_BASE}/v1/auth/google/authorize`
  }
  return `${window.location.origin}${API_BASE}/v1/auth/google/authorize`
}

function getExpectedOrigin(): string {
  if (API_BASE.startsWith('http')) {
    return new URL(API_BASE).origin
  }
  return window.location.origin
}

function isAuthResult(data: unknown): data is GoogleSignInResult {
  return typeof data === 'object' && data !== null && 'success' in data
}

/**
 * Open a centered popup for Google sign-in and wait for the API callback
 * page to post a message back to the parent window.
 */
export function signInWithGooglePopup(): Promise<GoogleSignInResult> {
  return new Promise((resolve, reject) => {
    const width = 500
    const height = 600
    const left = window.screenX + Math.round((window.outerWidth - width) / 2)
    const top = window.screenY + Math.round((window.outerHeight - height) / 2)

    const popup = window.open(
      getAuthUrl(),
      'googleSignIn',
      `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`
    )

    if (!popup) {
      reject(new Error('popup_blocked'))
      return
    }

    const expectedOrigin = getExpectedOrigin()
    let settled = false

    const cleanup = () => {
      settled = true
      window.removeEventListener('message', onMessage)
      window.clearInterval(checkClosed)
      if (!popup.closed) popup.close()
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return
      if (!isAuthResult(event.data)) return
      if (settled) return
      cleanup()
      resolve(event.data)
    }

    window.addEventListener('message', onMessage)

    const checkClosed = window.setInterval(() => {
      if (popup.closed) {
        cleanup()
        reject(new Error('popup_closed'))
      }
    }, 400)

    // Safety cap: close the popup if the user leaves the page open too long.
    window.setTimeout(() => {
      if (!settled) {
        cleanup()
        reject(new Error('popup_timeout'))
      }
    }, 120_000)
  })
}
